import { AppTypedFilters, FilterType, FilterTypeValue } from "@/components/filter/filter.types.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTimeOffCategoriesSearchInline } from "@/queries/use-time-off.query.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { FilterItem, Multiselect, SelectItem } from "@skailer-com/uikit";
import { useMemo } from "react";
import { Control, Controller, FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";

export const useAppFilterTimeOffRenders = ({
  control,
  typedFilters,
  errors,
  filters,
}: {
  control: Control;
  typedFilters: AppTypedFilters;
  errors: FieldErrors;
  filters: FilterItem<FilterTypeValue>[];
}) => {
  const { t } = useTranslation();
  const { canIEdit, isFeatureEnabled } = usePermissions();

  const canViewTimeOffCategories =
    filters.some((item) => item.type === FilterType.timeOffCategory) &&
    isFeatureEnabled(ApiFeatureType.time_off) &&
    (canIEdit(ApiPermission.manage_leave_policies) ||
      canIEdit(ApiPermission.reporting_leave_balance) ||
      canIEdit(ApiPermission.reporting_leave_used));
  const categories = useTimeOffCategoriesSearchInline({ enabled: canViewTimeOffCategories });

  const categoriesSelected = useMemo(() => {
    return categories.data?.filter((x) => typedFilters.category_ids?.includes(x.id)) || [];
  }, [typedFilters]);

  const renderFilterTimeOffCategory = (item: FilterItem<FilterTypeValue>) => {
    if (!canViewTimeOffCategories) return null;

    return (
      <Controller
        key={item.name}
        render={({ field: { onChange, value } }) => (
          <Multiselect
            label={item.label || t("time_off:category")}
            options={categoriesSelected as SelectItem[]}
            values={value}
            onChange={onChange}
            searchOptions={categories.options}
            onSearchOptions={categories.search}
            loadOptionsOnOpen
            searchable
            isClearable
            error={errors[item.name]?.message as string}
            isLoading={categories.isLoading}
            emptyTitle={t("common:select")}
          />
        )}
        control={control}
        name={item.name}
      />
    );
  };

  return {
    renderFilterTimeOffCategory,
  };
};
